import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiShield, FiLock, FiEye, FiDatabase, FiMail } from 'react-icons/fi';

export default function PrivacyPolicy() {
  const lastUpdated = "January 31, 2026";

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <FiArrowLeft className="w-4 h-4" />
            <span>Back to FirstCut</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">F</span>
            </div>
            <span className="text-white font-semibold">FirstCut</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
              <FiShield className="w-6 h-6 text-violet-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
              <p className="text-slate-400">Last updated: {lastUpdated}</p>
            </div>
          </div>
        </div>

        <div className="prose prose-invert prose-slate max-w-none">
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mb-8">
            <p className="text-slate-300 leading-relaxed">
              At FirstCut ("we", "our", or "us"), we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered content generation platform.
            </p>
          </div>

          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <FiDatabase className="w-5 h-5 text-violet-400" />
              <h2 className="text-xl font-semibold text-white m-0">1. Information We Collect</h2>
            </div>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5 space-y-4">
              <div>
                <h3 className="text-white font-medium mb-2">Personal Information</h3>
                <ul className="text-slate-300 space-y-2 list-disc pl-5">
                  <li>Name and email address (via Google OAuth)</li>
                  <li>Profile picture (from Google account)</li>
                  <li>Payment information (processed securely via Razorpay)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Business Information</h3>
                <ul className="text-slate-300 space-y-2 list-disc pl-5">
                  <li>Client/brand information you provide</li>
                  <li>Campaign and content data</li>
                  <li>Brand assets (logos, product images)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Usage Information</h3>
                <ul className="text-slate-300 space-y-2 list-disc pl-5">
                  <li>Content generation history</li>
                  <li>Approval/rejection preferences (to improve AI suggestions)</li>
                  <li>Platform usage analytics</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <FiEye className="w-5 h-5 text-violet-400" />
              <h2 className="text-xl font-semibold text-white m-0">2. How We Use Your Information</h2>
            </div>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5">
              <ul className="text-slate-300 space-y-3 list-disc pl-5">
                <li><strong>Service Delivery:</strong> To generate AI-powered content tailored to your brand</li>
                <li><strong>Personalization:</strong> To learn your preferences and improve content suggestions (70/30 learning system)</li>
                <li><strong>Novelty Checking:</strong> To ensure content freshness and prevent repetition</li>
                <li><strong>Account Management:</strong> To manage your subscription and usage credits</li>
                <li><strong>Communication:</strong> To send service updates and support responses</li>
                <li><strong>Analytics:</strong> To improve our platform and user experience</li>
              </ul>
            </div>
          </section>

          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <FiLock className="w-5 h-5 text-violet-400" />
              <h2 className="text-xl font-semibold text-white m-0">3. Data Security</h2>
            </div>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5">
              <p className="text-slate-300 mb-4">We implement industry-standard security measures:</p>
              <ul className="text-slate-300 space-y-2 list-disc pl-5">
                <li>Secure HTTPS encryption for all data transmission</li>
                <li>HttpOnly cookies for session management</li>
                <li>Secure payment processing via Razorpay (PCI-DSS compliant)</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and authentication</li>
              </ul>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">4. AI-Generated Content & Metadata</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5 space-y-4">
              <div>
                <h3 className="text-white font-medium mb-2">Content Credentials (C2PA)</h3>
                <p className="text-slate-300">
                  All AI-generated images from FirstCut include Content Credentials (C2PA metadata). This is an industry-standard for digital content provenance adopted by Adobe, Google, Microsoft, and others. This metadata:
                </p>
                <ul className="text-slate-300 space-y-2 list-disc pl-5 mt-2">
                  <li>Identifies the content as AI-generated or AI-enhanced</li>
                  <li>Is embedded invisibly in the image file</li>
                  <li>May be detected by social media platforms (Instagram, Facebook, etc.)</li>
                  <li>Cannot be removed by users as per our Terms of Service</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Photo Processing</h3>
                <p className="text-slate-300">
                  When you upload photos to FirstCut, they are processed by AI services (Google Gemini) to generate enhanced or styled content. Your original photos are stored securely and are not shared publicly or used for AI training without your consent.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">5. Data Sharing</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5">
              <p className="text-slate-300 mb-4">We do not sell your personal data. We may share information with:</p>
              <ul className="text-slate-300 space-y-2 list-disc pl-5">
                <li><strong>AI Service Providers:</strong> OpenAI and Google (for content and image generation)</li>
                <li><strong>Payment Processor:</strong> Razorpay (for subscription billing)</li>
                <li><strong>Authentication:</strong> Google OAuth (for secure login)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              </ul>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">6. Your Rights</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5">
              <p className="text-slate-300 mb-4">You have the right to:</p>
              <ul className="text-slate-300 space-y-2 list-disc pl-5">
                <li>Access your personal data</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your account and data</li>
                <li>Export your content and data</li>
                <li>Opt-out of marketing communications</li>
              </ul>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">7. Cookies</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5">
              <p className="text-slate-300">
                We use essential cookies for authentication and session management. These are necessary for the platform to function and cannot be disabled while using our service.
              </p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">8. Data Retention</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5">
              <p className="text-slate-300 mb-3">
                We retain your data for as long as your account is active or as needed to provide you with our services.
              </p>
              <ul className="text-slate-300 space-y-2 list-disc pl-5">
                <li><strong>Active Accounts:</strong> All data is retained while your account is active</li>
                <li><strong>Archived Clients:</strong> Client data is retained for 30 days in an archived state, allowing recovery</li>
                <li><strong>Account Deletion:</strong> To request account deletion and data removal, please contact us at support@firstcut.me</li>
              </ul>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">9. Changes to This Policy</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5">
              <p className="text-slate-300">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </div>
          </section>

          <section>
            <div className="flex items-center gap-3 mb-4">
              <FiMail className="w-5 h-5 text-violet-400" />
              <h2 className="text-xl font-semibold text-white m-0">10. Contact Us</h2>
            </div>
            <div className="bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/30 rounded-lg p-5">
              <p className="text-slate-300 mb-3">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <ul className="text-slate-300 space-y-2">
                <li><strong>Email:</strong> <a href="mailto:privacy@firstcut.me" className="text-violet-400 hover:text-violet-300">privacy@firstcut.me</a></li>
                <li><strong>Website:</strong> <a href="https://firstcut.me" className="text-violet-400 hover:text-violet-300">https://firstcut.me</a></li>
              </ul>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-slate-500 text-sm">
          <p>© 2026 FirstCut. All rights reserved.</p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/terms" className="hover:text-slate-300 transition-colors">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-slate-300 transition-colors">Privacy Policy</Link>
            <Link to="/refund" className="hover:text-slate-300 transition-colors">Refund Policy</Link>
            <Link to="/contact-us" className="hover:text-slate-300 transition-colors">Contact Us</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
